/**
 * User Store
 * Manages user list, search, and user-specific data
 */

import type { UserDTO, PaginatedResponse, PaginationMeta } from './types';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import { api } from '@/common/api';

/**
 * User store state
 */
export interface UserStoreState {
  users: UserDTO[];
  selectedUser: UserDTO | null;
  loading: boolean;
  error: Error | null;
  searchQuery: string;
  currentPage: number;
  pageSize: number;
  paginationMeta: PaginationMeta | null;

  // Actions
  setUsers: (users: UserDTO[]) => void;
  setSelectedUser: (user: UserDTO | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  setSearchQuery: (query: string) => void;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  addUser: (user: UserDTO) => void;
  updateUserInList: (user: UserDTO) => void;
  removeUserFromList: (userId: string) => void;

  // Async actions
  fetchUsers: (page?: number, pageSize?: number) => Promise<PaginatedResponse<UserDTO>>;
  fetchUserById: (id: string) => Promise<UserDTO>;
  searchUsers: (query: string, page?: number) => Promise<PaginatedResponse<UserDTO>>;
  createUser: (data: Partial<UserDTO>) => Promise<UserDTO>;
  updateUser: (id: string, data: Partial<UserDTO>) => Promise<UserDTO>;
  deleteUser: (id: string) => Promise<void>;

  // Helper methods
  getUserById: (id: string) => UserDTO | undefined;
  clearUserStore: () => void;
}

/**
 * User store
 */
export const useUserStore = create<UserStoreState>()(
  persist(
    immer((set, get) => ({
      // Initial state
      users: [],
      selectedUser: null,
      loading: false,
      error: null,
      searchQuery: '',
      currentPage: 1,
      pageSize: 20,
      paginationMeta: null,

      // Sync actions
      setUsers: (users) => {
        set((state) => {
          state.users = users;
        });
      },

      setSelectedUser: (user) => {
        set((state) => {
          state.selectedUser = user;
        });
      },

      setLoading: (loading) => {
        set((state) => {
          state.loading = loading;
        });
      },

      setError: (error) => {
        set((state) => {
          state.error = error;
        });
      },

      setSearchQuery: (query) => {
        set((state) => {
          state.searchQuery = query;
          state.currentPage = 1; // Reset to first page on new search
        });
      },

      setCurrentPage: (page) => {
        set((state) => {
          state.currentPage = page;
        });
      },

      setPageSize: (size) => {
        set((state) => {
          state.pageSize = size;
          state.currentPage = 1; // Reset to first page on page size change
        });
      },

      addUser: (user) => {
        set((state) => {
          state.users.unshift(user);
        });
      },

      updateUserInList: (user) => {
        set((state) => {
          const index = state.users.findIndex((u) => u.id === user.id);
          if (index !== -1) {
            state.users[index] = user;
          }
          if (state.selectedUser?.id === user.id) {
            state.selectedUser = user;
          }
        });
      },

      removeUserFromList: (userId) => {
        set((state) => {
          state.users = state.users.filter((u) => u.id !== userId);
          if (state.selectedUser?.id === userId) {
            state.selectedUser = null;
          }
        });
      },

      // Async actions
      fetchUsers: async (page = 1, pageSize = 20) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const response = await api.get<PaginatedResponse<UserDTO>>('/users', {
            params: {
              page,
              pageSize,
            },
          });

          set((state) => {
            state.users = response.items;
            state.paginationMeta = response.meta;
            state.currentPage = page;
            state.pageSize = pageSize;
            state.loading = false;
          });

          return response;
        } catch (error) {
          set((state) => {
            state.loading = false;
            state.error = error instanceof Error ? error : new Error('Failed to fetch users');
          });
          throw error;
        }
      },

      fetchUserById: async (id) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const user = await api.get<UserDTO>(`/users/${id}`);

          set((state) => {
            state.selectedUser = user;
            state.loading = false;
          });

          return user;
        } catch (error) {
          set((state) => {
            state.loading = false;
            state.error = error instanceof Error ? error : new Error('Failed to fetch user');
          });
          throw error;
        }
      },

      searchUsers: async (query, page = 1) => {
        set((state) => {
          state.loading = true;
          state.error = null;
          state.searchQuery = query;
        });

        try {
          const response = await api.get<PaginatedResponse<UserDTO>>('/users/search', {
            params: {
              q: query,
              page,
              pageSize: get().pageSize,
            },
          });

          set((state) => {
            state.users = response.items;
            state.paginationMeta = response.meta;
            state.currentPage = page;
            state.loading = false;
          });

          return response;
        } catch (error) {
          set((state) => {
            state.loading = false;
            state.error = error instanceof Error ? error : new Error('Search failed');
          });
          throw error;
        }
      },

      createUser: async (data) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const user = await api.post<UserDTO>('/users', data);

          set((state) => {
            state.users.unshift(user);
            state.loading = false;
          });

          return user;
        } catch (error) {
          set((state) => {
            state.loading = false;
            state.error = error instanceof Error ? error : new Error('Failed to create user');
          });
          throw error;
        }
      },

      updateUser: async (id, data) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const user = await api.patch<UserDTO>(`/users/${id}`, data);

          set((state) => {
            const index = state.users.findIndex((u) => u.id === id);
            if (index !== -1) {
              state.users[index] = user;
            }
            if (state.selectedUser?.id === id) {
              state.selectedUser = user;
            }
            state.loading = false;
          });

          return user;
        } catch (error) {
          set((state) => {
            state.loading = false;
            state.error = error instanceof Error ? error : new Error('Failed to update user');
          });
          throw error;
        }
      },

      deleteUser: async (id) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          await api.delete(`/users/${id}`);

          set((state) => {
            state.users = state.users.filter((u) => u.id !== id);
            if (state.selectedUser?.id === id) {
              state.selectedUser = null;
            }
            state.loading = false;
          });
        } catch (error) {
          set((state) => {
            state.loading = false;
            state.error = error instanceof Error ? error : new Error('Failed to delete user');
          });
          throw error;
        }
      },

      // Helper methods
      getUserById: (id) => {
        return get().users.find((u) => u.id === id);
      },

      clearUserStore: () => {
        set((state) => {
          state.users = [];
          state.selectedUser = null;
          state.searchQuery = '';
          state.currentPage = 1;
          state.paginationMeta = null;
          state.error = null;
        });
      },
    })),
    {
      name: 'user-store',
      version: 1,
      // Don't persist users to keep data fresh
      partialize: (state) => ({
        pageSize: state.pageSize,
        searchQuery: state.searchQuery,
      }),
    }
  )
);

/**
 * User store selectors
 */
export const useUsers = () => useUserStore((state) => state.users);
export const useSelectedUser = () => useUserStore((state) => state.selectedUser);
export const useUserLoading = () => useUserStore((state) => state.loading);
export const useUserError = () => useUserStore((state) => state.error);
export const useUserPagination = () =>
  useUserStore((state) => ({
    currentPage: state.currentPage,
    pageSize: state.pageSize,
    paginationMeta: state.paginationMeta,
  }));
export const useUserSearch = () => useUserStore((state) => state.searchQuery);
